/**
 * ShowFormCard (spec 075 — Phase 5).
 *
 * Deterministic renderer for a NAMED View (pre-authored form definition):
 * fields, labels, options, and consents come from versioned agent config —
 * never from the model — so a business form renders byte-identical every
 * session, in the session locale, RTL-aware.
 *
 * Renders in place of the composer (like AskQuestionCard) while the
 * `show_form` tool call is held open; Submit resolves the tool with the
 * STRUCTURED values object. Required fields gate the submit with inline,
 * localized errors — an incomplete form can never reach the agent.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { AgentViewDefinition, AgentViewField } from "@taqat/db";
import { getEmbedLocale } from "@/lib/embed-locale";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";

/** Pending show_form call held open by the chat. */
export interface PendingShowForm {
  toolCallId: string;
  input: { formId: string; prefill?: Record<string, string> };
}

interface ShowFormCardProps {
  pending: PendingShowForm;
  view: AgentViewDefinition;
  onSubmit: (values: Record<string, unknown>) => void;
  onDismiss: () => void;
  disabled?: boolean;
}

/** Chrome strings for the card itself (module-local — form labels come from the view). */
const CARD_STRINGS: Record<
  string,
  { required: string; submit: string; dismiss: string; fix: string; back: string; next: string; step: (x: number, y: number) => string }
> = {
  en: {
    required: "This field is required",
    submit: "Submit request",
    dismiss: "Cancel",
    fix: "Please complete the highlighted fields",
    back: "Back",
    next: "Next",
    step: (x, y) => `Step ${x} of ${y}`,
  },
  ar: {
    required: "هذا الحقل مطلوب",
    submit: "إرسال الطلب",
    dismiss: "إلغاء",
    fix: "يرجى إكمال الحقول المحددة",
    back: "رجوع",
    next: "التالي",
    step: (x, y) => `الخطوة ${x} من ${y}`,
  },
};

/** Locales authored right-to-left (controls the Back/Next chevron direction). */
const RTL_LOCALES = new Set(["ar", "he", "fa", "ur"]);

/** Resolve a per-locale string map for the active locale (en fallback). */
function loc(map: Record<string, string> | undefined, locale: string): string {
  if (!map) return "";
  return map[locale] ?? map.en ?? Object.values(map)[0] ?? "";
}
function locList(map: Record<string, string[]> | undefined, locale: string): string[] {
  if (!map) return [];
  return map[locale] ?? map.en ?? Object.values(map)[0] ?? [];
}

export function ShowFormCard({
  pending,
  view,
  onSubmit,
  onDismiss,
  disabled,
}: ShowFormCardProps) {
  const locale = getEmbedLocale() ?? "en";
  const t = CARD_STRINGS[locale] ?? CARD_STRINGS.en;
  const prefill = pending.input.prefill ?? {};

  /** Initial values: prefill where field keys match; multi-selects start empty. */
  const initialValues = useMemo(() => {
    const v: Record<string, unknown> = {};
    for (const f of view.fields) {
      if (f.kind === "multi-select") v[f.key] = [] as string[];
      else if (f.kind === "consent") v[f.key] = false;
      else v[f.key] = prefill[f.key] ?? "";
    }
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.id, pending.toolCallId]);

  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [triedSubmit, setTriedSubmit] = useState(false);

  const set = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  };

  const fieldMissing = (f: AgentViewField, v: unknown): boolean => {
    const required = f.required ?? f.kind === "consent";
    if (!required) return false;
    if (f.kind === "consent") return v !== true;
    if (f.kind === "multi-select") return !Array.isArray(v) || v.length === 0;
    return typeof v !== "string" || v.trim().length === 0;
  };

  // ── Dynamic multi-step wizard ────────────────────────────────────────
  // Steps are derived from the distinct `step` values on the fields (default
  // 1). When no field carries a step, there is exactly one step and the card
  // renders identically to the classic single-page form.
  const stepNumbers = useMemo(() => {
    const nums = Array.from(new Set(view.fields.map((f) => f.step ?? 1)));
    return nums.sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.id]);
  const isMultiStep = stepNumbers.length > 1;
  const isRtl = RTL_LOCALES.has(locale);
  const [stepIdx, setStepIdx] = useState(0);
  const currentStepNum = stepNumbers[stepIdx] ?? 1;
  const stepFields = view.fields.filter((f) => (f.step ?? 1) === currentStepNum);
  const stepTitle = loc(view.steps?.[stepIdx]?.title, locale);

  /** Collect the missing required fields among `fieldsToCheck`. */
  const collectErrors = (fieldsToCheck: AgentViewField[]): Record<string, boolean> => {
    const next: Record<string, boolean> = {};
    for (const f of fieldsToCheck) if (fieldMissing(f, values[f.key])) next[f.key] = true;
    return next;
  };

  /** Jump to the step that owns a given field key (used to surface errors). */
  const goToStepOf = (key: string) => {
    const f = view.fields.find((x) => x.key === key);
    const idx = stepNumbers.indexOf(f?.step ?? 1);
    if (idx >= 0) setStepIdx(idx);
  };

  const handleBack = () => {
    setErrors({});
    setTriedSubmit(false);
    setStepIdx((i) => Math.max(0, i - 1));
  };

  /** Validate only the current step before advancing. */
  const handleNext = () => {
    const next = collectErrors(stepFields);
    if (Object.keys(next).length > 0) {
      setTriedSubmit(true);
      setErrors(next);
      return;
    }
    setErrors({});
    setTriedSubmit(false);
    setStepIdx((i) => Math.min(stepNumbers.length - 1, i + 1));
  };

  const handleSubmit = () => {
    // Validate every field across all steps — a user may have navigated back
    // and cleared something after a step passed.
    const nextErrors = collectErrors(view.fields);
    setTriedSubmit(true);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstKey = view.fields.find((f) => nextErrors[f.key])?.key;
      if (firstKey) goToStepOf(firstKey);
      return;
    }

    // Phone kind: submit with the country prefix included.
    const out: Record<string, unknown> = { ...values };
    for (const f of view.fields) {
      if (f.kind === "phone" && typeof out[f.key] === "string") {
        const raw = (out[f.key] as string).trim();
        const cc = f.countryCode ?? "";
        out[f.key] = raw.startsWith("+") ? raw : `${cc} ${raw}`.trim();
      }
    }
    onSubmit(out);
  };

  const renderField = (f: AgentViewField) => {
    const label = loc(f.label, locale);
    const err = errors[f.key];
    const labelEl = (
      <span className="text-[12.5px] font-medium text-foreground">
        {label} {(f.required ?? f.kind === "consent") && <span className="text-destructive">*</span>}
      </span>
    );

    switch (f.kind) {
      case "multi-select": {
        const opts = locList(f.options, locale);
        const selected = (values[f.key] as string[]) ?? [];
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <div className="grid grid-cols-2 gap-1.5">
              {opts.map((opt) => {
                const on = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() =>
                      set(
                        f.key,
                        on ? selected.filter((o) => o !== opt) : [...selected, opt]
                      )
                    }
                    className={cn(
                      "flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[13px] transition-colors text-start",
                      on
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "grid h-4 w-4 shrink-0 place-items-center rounded border",
                        on ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/50"
                      )}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate">{opt}</span>
                  </button>
                );
              })}
            </div>
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
      }
      case "radio": {
        const opts = locList(f.options, locale);
        const current = values[f.key] as string;
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <div className="flex flex-wrap gap-1.5">
              {opts.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => set(f.key, opt)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-[13px] transition-colors",
                    current === opt
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  )}
                  dir="ltr"
                >
                  {opt}
                </button>
              ))}
            </div>
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
      }
      case "phone":
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <div className="flex items-center gap-1.5" dir="ltr">
              {f.countryCode && (
                <span className="rounded-md border border-border bg-muted px-2 py-1.5 text-[13px] text-muted-foreground">
                  {f.countryCode}
                </span>
              )}
              <Input
                dir="ltr"
                inputMode="tel"
                value={String(values[f.key] ?? "").replace(f.countryCode ?? "", "").trim()}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={loc(f.placeholder, locale) || "5xxx xxxx"}
                className={cn("h-8 text-sm", err && "border-destructive")}
              />
            </div>
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
      case "date":
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <Input
              type="date"
              dir="ltr"
              value={String(values[f.key] ?? "")}
              onChange={(e) => set(f.key, e.target.value)}
              className={cn("h-8 text-sm", err && "border-destructive")}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
      case "consent":
        return (
          <div key={f.key} className="space-y-1">
            <label className="flex items-start gap-2 text-[12px] leading-snug text-foreground cursor-pointer">
              <button
                type="button"
                role="checkbox"
                aria-checked={values[f.key] === true}
                onClick={() => set(f.key, values[f.key] !== true)}
                className={cn(
                  "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border",
                  values[f.key] === true
                    ? "border-primary bg-primary text-primary-foreground"
                    : err
                      ? "border-destructive"
                      : "border-muted-foreground/50"
                )}
              >
                {values[f.key] === true && <Check className="h-3 w-3" />}
              </button>
              <span>
                {label}{" "}
                {f.linkUrl && (
                  <a
                    href={f.linkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    ↗
                  </a>
                )}
              </span>
            </label>
            {err && <p className="text-[11.5px] text-destructive ms-6">{t.required}</p>}
          </div>
        );
      case "textarea":
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <textarea
              value={String(values[f.key] ?? "")}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={loc(f.placeholder, locale)}
              rows={3}
              className={cn(
                "w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring",
                err && "border-destructive"
              )}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
      default: // text / select fallback to text input
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <Input
              value={String(values[f.key] ?? "")}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={loc(f.placeholder, locale)}
              className={cn("h-8 text-sm", err && "border-destructive")}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        );
    }
  };

  return (
    <div
      data-testid="show-form-card"
      className="mx-auto flex w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {loc(view.title, locale)}
          </p>
          {view.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {loc(view.description, locale)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          title={t.dismiss}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress header — only when the form is split into multiple steps. */}
      {isMultiStep && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <div className="flex gap-1" data-testid="show-form-progress">
            {stepNumbers.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === stepIdx
                    ? "w-5 bg-primary"
                    : i < stepIdx
                      ? "w-1.5 bg-primary/60"
                      : "w-1.5 bg-muted-foreground/30"
                )}
              />
            ))}
          </div>
          <span className="text-[11.5px] text-muted-foreground">
            {t.step(stepIdx + 1, stepNumbers.length)}
            {stepTitle ? ` · ${stepTitle}` : ""}
          </span>
        </div>
      )}

      <div className="max-h-[52vh] space-y-3.5 overflow-y-auto px-4 py-3.5">
        {stepFields.filter((f) => f.kind !== "consent").map(renderField)}

        {/* The form-level notice appears on the final step, just above any
            consents — shown whether or not the form has consent fields. */}
        {stepIdx === stepNumbers.length - 1 && view.notice && (
          <div className="rounded-md bg-amber-500/10 px-3 py-2 text-[11.5px] leading-snug text-amber-700 dark:text-amber-400">
            ⚠ {loc(view.notice, locale)}
          </div>
        )}

        {stepFields.filter((f) => f.kind === "consent").map(renderField)}
      </div>

      <div className="border-t border-border px-4 py-3">
        {triedSubmit && Object.values(errors).some(Boolean) && (
          <p className="mb-2 text-[11.5px] text-destructive">{t.fix}</p>
        )}
        {isMultiStep ? (
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="gap-1"
                data-testid="show-form-back"
              >
                {isRtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                {t.back}
              </Button>
            )}
            {stepIdx < stepNumbers.length - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 gap-1"
                data-testid="show-form-next"
              >
                {t.next}
                {isRtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            ) : (
              <Button
                className="flex-1"
                onClick={handleSubmit}
                disabled={disabled}
                data-testid="show-form-submit"
              >
                {loc(view.submitLabel, locale) || t.submit}
              </Button>
            )}
          </div>
        ) : (
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={disabled}
            data-testid="show-form-submit"
          >
            {loc(view.submitLabel, locale) || t.submit}
          </Button>
        )}
      </div>
    </div>
  );
}
